import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Form } from 'react-bootstrap';
import { Line, Bar, Doughnut, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { getDashboardData, getYearOverYearComparison } from '../../../services/admin';
import moment from 'moment';
import { toast } from 'react-toastify';
import Loading from '../../common/Loading';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const ManagementDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [yoyData, setYoyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    branch_id: null,
    date_from: moment().startOf('month').format('YYYY-MM-DD'),
    date_to: moment().format('YYYY-MM-DD'),
  });

  useEffect(() => {
    loadDashboardData();
    loadYoYData();
  }, [filters]);

  const loadDashboardData = () => {
    setLoading(true);
    getDashboardData(filters)
      .then((res) => {
        setDashboardData(res.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        toast.error('Failed to load dashboard data');
        setLoading(false);
      });
  };

  const loadYoYData = () => {
    getYearOverYearComparison({ branch_id: filters.branch_id })
      .then((res) => {
        setYoyData(res.data.comparison);
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value,
    });
  };

  const handleRefresh = () => {
    loadDashboardData();
    loadYoYData();
  };

  if (loading || !dashboardData) {
    return <Loading />;
  }

  const { financial_kpis, revenue_expense_trend, budget_utilization, loan_portfolio_health, asset_summary, cash_position } = dashboardData;

  // Revenue & Expense Trend Chart Data
  const revenueTrendData = {
    labels: revenue_expense_trend?.labels,
    datasets: [
      {
        label: 'Revenue',
        data: revenue_expense_trend?.revenue,
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        tension: 0.4,
      },
      {
        label: 'Expense',
        data: revenue_expense_trend?.expense,
        borderColor: 'rgb(255, 99, 132)',
        backgroundColor: 'rgba(255, 99, 132, 0.2)',
        tension: 0.4,
      },
      {
        label: 'Profit',
        data: revenue_expense_trend?.profit,
        borderColor: 'rgb(54, 162, 235)',
        backgroundColor: 'rgba(54, 162, 235, 0.2)',
        tension: 0.4,
      },
    ],
  };

  // Budget Utilization Chart
  const budgetUtilizationData = {
    labels: ['On Track', 'Warning', 'Exceeded'],
    datasets: [
      {
        data: [budget_utilization?.on_track, budget_utilization?.warning, budget_utilization?.exceeded],
        backgroundColor: ['#28a745', '#ffc107', '#dc3545'],
      },
    ],
  };

  // Loan Portfolio Chart
  const loanPortfolioData = {
    labels: ['Active Loans', 'Closed Loans'],
    datasets: [
      {
        data: [loan_portfolio_health?.active_loans, loan_portfolio_health?.closed_loans],
        backgroundColor: ['#007bff', '#6c757d'],
      },
    ],
  };

  // Year-over-Year Comparison Chart
  const yoyChartData = yoyData ? {
    labels: yoyData.map(d => d.month),
    datasets: [
      {
        label: `${moment().year()} Revenue`,
        data: yoyData.map(d => d.current_year.income),
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
      },
      {
        label: `${moment().year() - 1} Revenue`,
        data: yoyData.map(d => d.previous_year.income),
        borderColor: 'rgb(153, 102, 255)',
        backgroundColor: 'rgba(153, 102, 255, 0.2)',
      },
    ],
  } : null;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getGrowthIndicator = (value) => {
    if (value > 0) {
      return <span className="text-success"><i className="fa fa-arrow-up"></i> {value.toFixed(2)}%</span>;
    } else if (value < 0) {
      return <span className="text-danger"><i className="fa fa-arrow-down"></i> {Math.abs(value).toFixed(2)}%</span>;
    } else {
      return <span className="text-muted">0%</span>;
    }
  };

  return (
    <div className="main-container container-fluid">
      <div className="page-header">
        <div>
          <h1 className="page-title">Management Dashboard</h1>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="/superadmin/dashboard">Dashboard</a></li>
            <li className="breadcrumb-item active" aria-current="page">Analytics</li>
          </ol>
        </div>
        <div className="ms-auto">
          <button className="btn btn-primary" onClick={handleRefresh}>
            <i className="fa fa-refresh me-2"></i>Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-3">
        <Card.Body>
          <Row>
            <Col md={4}>
              <Form.Group>
                <Form.Label>From Date</Form.Label>
                <Form.Control
                  type="date"
                  name="date_from"
                  value={filters.date_from}
                  onChange={handleFilterChange}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>To Date</Form.Label>
                <Form.Control
                  type="date"
                  name="date_to"
                  value={filters.date_to}
                  onChange={handleFilterChange}
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Financial KPIs */}
      <Row className="mb-4">
        <Col xl={3} lg={6} md={6} sm={12}>
          <Card className="card-stats">
            <Card.Body>
              <div className="d-flex">
                <div className="flex-grow-1">
                  <h6 className="text-muted mb-2">Total Revenue</h6>
                  <h3 className="mb-0">{formatCurrency(financial_kpis?.total_revenue)}</h3>
                  <small className="text-muted">
                    {getGrowthIndicator(financial_kpis?.revenue_growth)} vs previous period
                  </small>
                </div>
                <div className="ms-auto">
                  <div className="icon-service bg-success-transparent text-success">
                    <i className="fa fa-line-chart"></i>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xl={3} lg={6} md={6} sm={12}>
          <Card className="card-stats">
            <Card.Body>
              <div className="d-flex">
                <div className="flex-grow-1">
                  <h6 className="text-muted mb-2">Total Expense</h6>
                  <h3 className="mb-0">{formatCurrency(financial_kpis?.total_expense)}</h3>
                  <small className="text-muted">
                    {getGrowthIndicator(financial_kpis?.expense_growth)} vs previous period
                  </small>
                </div>
                <div className="ms-auto">
                  <div className="icon-service bg-danger-transparent text-danger">
                    <i className="fa fa-money"></i>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xl={3} lg={6} md={6} sm={12}>
          <Card className="card-stats">
            <Card.Body>
              <div className="d-flex">
                <div className="flex-grow-1">
                  <h6 className="text-muted mb-2">Net Profit</h6>
                  <h3 className={`mb-0 ${financial_kpis?.net_profit >= 0 ? 'text-success' : 'text-danger'}`}>
                    {formatCurrency(financial_kpis?.net_profit)}
                  </h3>
                  <small className="text-muted">
                    {getGrowthIndicator(financial_kpis?.profit_growth)} vs previous period
                  </small>
                </div>
                <div className="ms-auto">
                  <div className={`icon-service ${financial_kpis?.net_profit >= 0 ? 'bg-success-transparent text-success' : 'bg-danger-transparent text-danger'}`}>
                    <i className="fa fa-trophy"></i>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xl={3} lg={6} md={6} sm={12}>
          <Card className="card-stats">
            <Card.Body>
              <div className="d-flex">
                <div className="flex-grow-1">
                  <h6 className="text-muted mb-2">Profit Margin</h6>
                  <h3 className="mb-0">{financial_kpis?.profit_margin?.toFixed(2)}%</h3>
                  <small className="text-muted">
                    Current period margin
                  </small>
                </div>
                <div className="ms-auto">
                  <div className="icon-service bg-primary-transparent text-primary">
                    <i className="fa fa-percent"></i>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Cash Position */}
      <Row className="mb-4">
        <Col xl={4} lg={4} md={6} sm={12}>
          <Card>
            <Card.Body>
              <h5 className="card-title">Cash Position</h5>
              <div className="mt-3">
                <div className="d-flex justify-content-between mb-2">
                  <span>Cash in Hand:</span>
                  <strong>{formatCurrency(cash_position?.cash_in_hand)}</strong>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span>Bank Balance:</span>
                  <strong>{formatCurrency(cash_position?.bank_balance)}</strong>
                </div>
                <hr />
                <div className="d-flex justify-content-between">
                  <span><strong>Total Liquid Assets:</strong></span>
                  <strong className="text-primary">{formatCurrency(cash_position?.total_liquid_assets)}</strong>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xl={4} lg={4} md={6} sm={12}>
          <Card>
            <Card.Body>
              <h5 className="card-title">Budget Utilization</h5>
              <div className="mt-3">
                <div className="d-flex justify-content-between mb-2">
                  <span>Total Budgeted:</span>
                  <strong>{formatCurrency(budget_utilization?.total_budgeted)}</strong>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span>Total Actual:</span>
                  <strong>{formatCurrency(budget_utilization?.total_actual)}</strong>
                </div>
                <hr />
                <div className="d-flex justify-content-between">
                  <span><strong>Utilization:</strong></span>
                  <strong className={budget_utilization?.utilization_percentage > 100 ? 'text-danger' : 'text-success'}>
                    {budget_utilization?.utilization_percentage?.toFixed(2)}%
                  </strong>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xl={4} lg={4} md={6} sm={12}>
          <Card>
            <Card.Body>
              <h5 className="card-title">Fixed Assets</h5>
              <div className="mt-3">
                <div className="d-flex justify-content-between mb-2">
                  <span>Total Cost:</span>
                  <strong>{formatCurrency(asset_summary?.total_cost)}</strong>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span>Accumulated Depreciation:</span>
                  <strong className="text-danger">{formatCurrency(asset_summary?.accumulated_depreciation)}</strong>
                </div>
                <hr />
                <div className="d-flex justify-content-between">
                  <span><strong>Net Book Value:</strong></span>
                  <strong className="text-primary">{formatCurrency(asset_summary?.net_book_value)}</strong>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Charts */}
      <Row className="mb-4">
        <Col xl={8} lg={12}>
          <Card>
            <Card.Header>
              <h4 className="card-title">Revenue & Expense Trend (Last 12 Months)</h4>
            </Card.Header>
            <Card.Body>
              <Line data={revenueTrendData} options={{ responsive: true, maintainAspectRatio: true }} />
            </Card.Body>
          </Card>
        </Col>

        <Col xl={4} lg={6}>
          <Card>
            <Card.Header>
              <h4 className="card-title">Budget Status</h4>
            </Card.Header>
            <Card.Body>
              <Doughnut data={budgetUtilizationData} options={{ responsive: true, maintainAspectRatio: true }} />
              <div className="mt-3 text-center">
                <small className="text-muted">Total Budgets: {budget_utilization?.total_budgets}</small>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Loan Portfolio & YoY Comparison */}
      <Row className="mb-4">
        <Col xl={4} lg={6}>
          <Card>
            <Card.Header>
              <h4 className="card-title">Loan Portfolio</h4>
            </Card.Header>
            <Card.Body>
              <Pie data={loanPortfolioData} options={{ responsive: true, maintainAspectRatio: true }} />
              <div className="mt-3">
                <div className="d-flex justify-content-between mb-1">
                  <small>Total Disbursed:</small>
                  <strong>{formatCurrency(loan_portfolio_health?.total_disbursed)}</strong>
                </div>
                <div className="d-flex justify-content-between mb-1">
                  <small>Total Collected:</small>
                  <strong>{formatCurrency(loan_portfolio_health?.total_collected)}</strong>
                </div>
                <div className="d-flex justify-content-between mb-1">
                  <small>Outstanding:</small>
                  <strong className="text-warning">{formatCurrency(loan_portfolio_health?.total_outstanding)}</strong>
                </div>
                <div className="d-flex justify-content-between">
                  <small>Collection Rate:</small>
                  <strong className="text-success">{loan_portfolio_health?.collection_rate?.toFixed(2)}%</strong>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xl={8} lg={12}>
          <Card>
            <Card.Header>
              <h4 className="card-title">Year-over-Year Comparison</h4>
            </Card.Header>
            <Card.Body>
              {yoyChartData && (
                <Bar data={yoyChartData} options={{ responsive: true, maintainAspectRatio: true }} />
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ManagementDashboard;
